import Link from "next/link";
import { Clock } from "lucide-react";
import type { BriefingContent } from "../types";

interface WasDanachBlockProps {
  data: BriefingContent;
}

export default function WasDanachBlock({ data }: WasDanachBlockProps) {
  const { wasDanach, assetChecklist } = data;

  return (
    <div id="warum-json" className="py-16 border-t border-gray-100">
      <span className="eyebrow eyebrow-blue">{wasDanach.eyebrow}</span>
      <h2 className="h-xl mt-2">{wasDanach.title}</h2>
      <p className="text-[15px] text-gray-700 leading-relaxed max-w-2xl mb-6">{wasDanach.intro}</p>

      {/* Sales-facing effort figure. Deliberately states whose effort it is —
          the questionnaire promises the client 5 minutes, so an unqualified
          "8 Stunden" here would read as a contradiction. */}
      <div className="flex items-start gap-3 rounded-xl border border-abis-primary/20 bg-abis-primary/5 px-4 py-3.5 mb-8 max-w-2xl">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-abis-primary/10 flex-shrink-0">
          <Clock className="w-4 h-4 text-abis-primary" aria-hidden />
        </span>
        <div>
          <p className="text-sm font-semibold text-abis-primary">
            {wasDanach.effort.value} · {wasDanach.effort.label}
          </p>
          <p className="text-[13px] text-gray-600 leading-relaxed mt-0.5">{wasDanach.effort.note}</p>
        </div>
      </div>

      <ol className="grid gap-4 sm:grid-cols-2 mb-10 list-none p-0">
        {wasDanach.steps.map((step) => (
          <li
            key={step.step}
            className="rounded-xl border border-gray-200 bg-white p-4"
          >
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-abis-primary/10 text-abis-primary text-xs font-bold tabular-nums mb-2">
              {step.step}
            </span>
            <h3 className="text-sm font-semibold text-abis-primary mb-1">{step.title}</h3>
            <p className="text-[13px] text-gray-600 leading-relaxed">{step.text}</p>
          </li>
        ))}
      </ol>

      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <p className="text-[15px] text-gray-700 leading-relaxed">{wasDanach.outro}</p>
          <Link
            href={wasDanach.relatedServiceHref}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-abis-primary hover:underline mt-3"
          >
            {wasDanach.relatedServiceLabel} →
          </Link>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            {assetChecklist.title}
          </h3>
          <ul className="space-y-2.5">
            {assetChecklist.items.map((item) => (
              <li key={item.label} className="flex gap-2 text-sm text-gray-700">
                <span className="text-abis-accent font-bold flex-shrink-0">·</span>
                <span>
                  <strong className="font-semibold text-gray-900">{item.label}</strong>{" "}
                  <span className="text-gray-500">{item.detail}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
